/**
 * Skyyrizon Analytics Bridge — lightweight click/submit event capture.
 *
 * Phase 5 (Commerce Brain) foundation. Today it:
 *   - Listens for clicks on any element carrying [data-sky-event]
 *   - Listens for submits on forms carrying [data-sky-event]
 *   - Enriches events with product id/handle/section context from
 *     data-sky-product-id / data-sky-product-handle / data-sky-section
 *     (searched up the DOM tree, so containers can carry the context)
 *   - console.log's events in development (theme editor / localhost / ?sky_debug)
 *   - Buffers events in memory and exposes window.SkyyrizonAnalytics
 *
 * Later phases connect the flush() transport to /api/skyyrizon/analytics
 * (Commerce Brain). Until an endpoint is configured, nothing leaves the
 * browser — no network calls, no Shopify behavior changes.
 *
 * TODO(Phase 5 — Commerce Brain): set ENDPOINT, add sendBeacon flush on
 * pagehide, add view events (shipping_block_viewed, faq_opened) via
 * IntersectionObserver + details[toggle].
 */
(function () {
  'use strict';

  var ENDPOINT = null; // e.g. '/api/skyyrizon/analytics' — Phase 5
  var buffer = [];
  var MAX_BUFFER = 100;

  var isDev =
    (window.Shopify && window.Shopify.designMode) ||
    /127\.0\.0\.1|localhost|\.myshopify\.com/.test(window.location.hostname) ||
    /[?&]sky_debug/.test(window.location.search);

  function contextFrom(el) {
    var ctx = {};
    var node = el;
    while (node && node !== document.documentElement) {
      if (ctx.productId === undefined && node.hasAttribute && node.hasAttribute('data-sky-product-id')) {
        ctx.productId = node.getAttribute('data-sky-product-id');
      }
      if (ctx.productHandle === undefined && node.hasAttribute && node.hasAttribute('data-sky-product-handle')) {
        ctx.productHandle = node.getAttribute('data-sky-product-handle');
      }
      if (ctx.section === undefined && node.hasAttribute && node.hasAttribute('data-sky-section')) {
        ctx.section = node.getAttribute('data-sky-section');
      }
      node = node.parentElement;
    }
    return ctx;
  }

  function track(name, el, extra) {
    var event = {
      event: name,
      ts: Date.now(),
      path: window.location.pathname,
      referrer: document.referrer || null,
    };
    if (el) {
      var ctx = contextFrom(el);
      if (ctx.productId) event.product_id = ctx.productId;
      if (ctx.productHandle) event.product_handle = ctx.productHandle;
      if (ctx.section) event.section = ctx.section;
    }
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) event[k] = extra[k];
      }
    }

    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer.shift();

    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[sky-analytics]', event.event, event);
    }

    if (ENDPOINT) {
      try {
        var body = JSON.stringify(event);
        if (navigator.sendBeacon) {
          navigator.sendBeacon(ENDPOINT, body);
        } else {
          fetch(ENDPOINT, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: body });
        }
      } catch (e) { /* analytics must never break shopping */ }
    }
  }

  // Clicks — delegated so dynamically added cards still fire
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-sky-event]') : null;
    if (!el || el.tagName === 'FORM') return;
    track(el.getAttribute('data-sky-event'), el);
  }, true);

  // Form submits (quick add, newsletter)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-sky-event')) return;
    track(form.getAttribute('data-sky-event'), form);
  }, true);

  // Product page: fire view_product + pdp_variant_view once on load.
  // Landing pages additionally fire view_product_landing_page + landing_page_source.
  function firePageViews() {
    var pdp = document.querySelector('[data-sky-pdp]');
    if (pdp) {
      track('view_product', pdp);
      if (pdp.getAttribute('data-sky-pdp-variant')) {
        track('pdp_variant_view', pdp, { pdp_variant: pdp.getAttribute('data-sky-pdp-variant') });
      }
      if (pdp.getAttribute('data-sky-page-type') === 'product-landing') {
        track('view_product_landing_page', pdp, {
          landing_mode: pdp.getAttribute('data-sky-landing-mode'),
          campaign_name: pdp.getAttribute('data-sky-campaign-name'),
          traffic_source: pdp.getAttribute('data-sky-traffic-source'),
          content_angle: pdp.getAttribute('data-sky-content-angle')
        });
        track('landing_page_source', pdp, {
          traffic_source: pdp.getAttribute('data-sky-traffic-source'),
          campaign_name: pdp.getAttribute('data-sky-campaign-name')
        });
      }
    }
  }

  // Related-products clicks (Dawn section) → related_product_click
  function initRelatedTracking() {
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!link) return;
      var inRelated = link.closest('.related-products, [id*="related"]');
      if (inRelated) track('related_product_click', link);
    }, true);
  }

  // Fire a section's data-sky-event once when it scrolls into view
  function initViewObservers() {
    var nodes = document.querySelectorAll('[data-sky-observe][data-sky-event]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el._skyFired) return;
        el._skyFired = true;
        track(el.getAttribute('data-sky-event'), el);
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  function boot() { firePageViews(); initViewObservers(); initRelatedTracking(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.SkyyrizonAnalytics = {
    track: track,
    events: function () { return buffer.slice(); },
    setEndpoint: function (url) { ENDPOINT = url; },
  };
})();
